import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@frontierag/frontier-ui-library';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ShoppingBasketOutlined as ShoppingBasketOutlinedIcon,
} from '@frontierag/frontier-ui-library/icons';
import { PRODUCT_PRICE_CURRENCY } from 'appConstants';
import { MAX_CONTENT_WIDTH } from 'appConstants';
import useOrderWizard from 'components/OrderWizard/useOrderWizard';
import ProductCard from 'components/ProductCard';
import { useConfirmDialog } from 'contexts/ConfirmDialog';
import { useField } from 'formik';
import EmptyState from 'shared/EmptyState';
import { useNotification } from 'shared/Notification';
import { formatPriceValue } from 'utils';

import ProductFormFields from '../ProductFormFields';
import EditProductDialog from './EditProductDialog';

const ViewBasket = () => {
  const { updateLastDeliveryDetails } = useOrderWizard();
  const { t } = useTranslation('CreateOrder');
  const { notification } = useNotification();
  const confirmDialog = useConfirmDialog();
  const [open, setOpen] = useState<boolean>(false);

  const toggleOpenDialog = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const [{ value: products = [] }, { error }, { setValue: setProducts }] = useField('products');
  const [{ value: debtOwner }] = useField('debtOwner');
  const [
    {
      value: { customerReference = '' },
    },
  ] = useField('customer');
  const [product, setProduct] = useState();

  const handleEditProduct = useCallback(
    (product) => {
      setProduct(product);
      toggleOpenDialog();
    },
    [toggleOpenDialog],
  );

  const handleDeleteProduct = useCallback(
    async (product) => {
      const { confirmed } = await confirmDialog({
        title: t('viewBasket.deleteDialog.title'),
        message: t('viewBasket.deleteDialog.message'),
        onActionLabel: t('viewBasket.deleteDialog.actionBtn'),
      });
      if (!confirmed) return;

      setProducts(
        products.filter((_product) => _product.internalOrderId !== product.internalOrderId),
      );
    },
    [products, setProducts, t, confirmDialog],
  );

  const editButtonAction = useMemo(
      () => ({
        title: t('viewBasket.card.editBtn'),
        onActionClick: handleEditProduct,
        icon: <EditIcon fontSize="small" />,
      }),
      [handleEditProduct, t],
    ),
    deleteButtonAction = useMemo(
      () => ({
        title: t('viewBasket.card.deleteBtn'),
        onActionClick: handleDeleteProduct,
        icon: <DeleteIcon fontSize="small" />,
      }),
      [handleDeleteProduct, t],
    );

  const handleSaveProduct = useCallback(
    (newProduct) => {
      setProducts(
        products.map((product) =>
          product.internalOrderId === newProduct.internalOrderId ? newProduct : product,
        ),
      );
      toggleOpenDialog();
      updateLastDeliveryDetails(newProduct);
      notification(t('ProductForm.notifications.productWasUpdated'), 'success');
    },
    [toggleOpenDialog, products, setProducts, updateLastDeliveryDetails, notification, t],
  );

  const formattedPrice = (price) => (
    <>
      <Box pr={0.5} component="span">
        {PRODUCT_PRICE_CURRENCY}
      </Box>
      {formatPriceValue(price)}
    </>
  );

  return (
    <Box display="flex" data-testid="view-basket-tab">
      {products && products.length > 0 ? (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: MAX_CONTENT_WIDTH,
              minWidth: MAX_CONTENT_WIDTH,
            }}
          >
            {products.map((product, key) => (
              <ProductCard
                key={product.internalOrderId}
                product={product}
                title={product.productData.packagedItemDescription}
                quantity={product.packagedItemQty}
                price={formattedPrice(product.packagedItemPrice)}
                actions={[editButtonAction, deleteButtonAction]}
                hasError={Boolean(error && error[key])}
              />
            ))}
          </Box>

          {product && open && (
            <ProductFormFields
              onSubmit={handleSaveProduct}
              product={product}
              isDebtOwner={debtOwner === 'true'}
              isEdit
            >
              <EditProductDialog
                open={open}
                onClose={toggleOpenDialog}
                customerReference={customerReference}
                isDebtOwner={debtOwner === 'true'}
              />
            </ProductFormFields>
          )}
        </>
      ) : (
        <EmptyState title={t('viewBasket.card.noProducts')} icon={<ShoppingBasketOutlinedIcon />} />
      )}
    </Box>
  );
};

export default ViewBasket;
